import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, Outlet } from 'react-router-dom';

import { CanvaContextProvider } from './context/CanvaContext';
import { WhitelistProvider } from './context/WhitelistContext';
import { AppProvider } from './components/context/AppContext';
import { SignupProvider } from './components/context/SignupContext';
import { CreatorProvider } from './components/context/CreatorContext';
import { ArtistProvider } from './components/context/ArtistContext';
import { InfluencerProvider } from './components/context/InfluencerContext';
import { FintueArtstyleProvider } from './components/context/FinetunContext';
import ArtStyleAgreement from './components/signup/FinetuneModel/ArtStyleAgreement';
import Blogs from './landingPage/Blogs/Blogs';
import BlogDetails from './landingPage/Blogs/BlogDetails';
import { BlogProvider } from './components/context/BlogContext';
import LandingPage from './landingPage/LandingPage';
import OriginalLandingPage from './landingPage/OriginalLandingPage';
import B2BPage from './landingPage/B2BPage';
import { PUBLIC_SIGN_IN_ENABLED } from './constants/publicAuth';
import ChoosePlan from './components/signup/Plan/ChoosePlan';
import CanvasAIEditor from './partials/CanvasAIEditor';
import SLPArtists from './landingPage/SLPArtists/SLPArtists';
import SLPInfluencers from './landingPage/SLPInfluencers/SLPInfluencers';

import CopyrightLicenseAgreementArtist from './landingPage/LicenseAgreements/CopyrightLicenseAgreementArtist';
import CopyrightLicenseAgreementCustomer from './landingPage/LicenseAgreements/CopyrightLicenseAgreementCustomer';
import LicenseAgreementInfluencer from './landingPage/LicenseAgreements/LicenseAgreementInfluencer';
import LicenseAgreementCustomer from './landingPage/LicenseAgreements/LicenseAgreementCustomer';
import ModalVideoPage from './pages/ModelsVideoPage';

const Loading = lazy(() => import('./components/common/Loading'));
const FrameLayout = lazy(() => import('./components/common/FrameLayout'));
const SignupFrameLayout = lazy(() => import('./components/signup/common/SignupFrameLayout'));

const CanvaGenerate = lazy(() => import('./pages/CanvaGenerate'));
const CanvaGenerateV2 = lazy(() => import('./pages/CanvaGenerateV2'));
const PersonasSelectionPage = lazy(() => import('./pages/PersonasSelectionPage'));
const ArtStylesSelectionPage = lazy(() => import('./pages/ArtStylesSelectionPage'));
const DashboardImages = lazy(() => import('./pages/DashboardImages'));
const GeneratedImageDetail = lazy(() => import('./pages/GeneratedImageDetail'));

const SignIn = lazy(() => import('./pages/SignIn'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Callback = lazy(() => import('./pages/Callback'));
const ModelsImagePage = lazy(() => import('./pages/ModelsImagePage'));
const MultiModelSupport=lazy(() => import('./components/MultiModel/MultimodelSupportForm'));

const FeatureManagementPage = lazy(() => import('./pages/Admin/FeatureManagement/FeatureManagement'));

const SignupAs = lazy(() => import('./components/signup/SignupAs/SignupAs'));
const SignupAccountType = lazy(() => import('./components/signup/AccountType/AccountType'));
const SignupChooseCategory = lazy(() => import('./components/signup/ChooseCategories/ChooseCategories'));
const SignupChooseCollaboration = lazy(() => import('./components/signup/ChooseCollaborations/ChooseCollaborations'));
const SignupUploadCoverPhoto = lazy(() => import('./components/signup/UploadCoverPhoto/UploadCoverPhoto'));
const SignupUploadHeadshot = lazy(() => import('./components/signup/UploadHeadshot/UploadHeadshot'));
const SignupSocialAccounts = lazy(() => import('./components/signup/SocialAccounts/SocialAccounts'));
const SignupPricingSetup = lazy(() => import('./components/signup/PricingSetup/PricingSetup'));
const SignupFinetuneModel = lazy(() => import('./components/signup/FinetuneModel/FinetuneModel'));
const SignupArtStylePricing = lazy(() => import('./components/signup/FinetuneModel/ArtStylePricing/ArtStylePricing'));

const InfluencerDashboard = lazy(() => import('./components/influencer/InfluencerDashboard/InfluencerDashboard'));
const InfluencerProfileEdit = lazy(() => import('./components/influencer/InfluencerProfile/InfluencerProfileEdit'));
const InfluencerLicenseRequest = lazy(() => import('./components/influencer/License/LicenseRequest'));
const InfluencerContentDetail = lazy(() => import('./components/influencer/License/InfluencerContentDetail'));
const GeneratedImageApprovalDetails = lazy(() => import('./components/influencer/License/GeneratedImagesApprovalDetails'));

const CreatorDashboard = lazy(() => import('./components/creator/Dashboard/CreatorDashboard'));
const CreatorProfileEdit = lazy(() => import('./components/creator/CreatorProfile/CreatorProfileEdit'));
const CreatorProject = lazy(() => import('./components/creator/Project/Project'));
const CreatorContentDetail = lazy(() => import('./components/creator/Project/CreatorContentDetail'));
const ViewInfluencer = lazy(() => import('./components/creator/ViewInfluencer/Influencer'));
const CreateProject = lazy(() => import('./components/creator/CreateProject/CreateProject'));
const Marketplace = lazy(() => import('./components/creator/Marketplace/Marketplace'));
const GeneratedImageCardDetail = lazy(() => import('./components/creator/Project/GeneratedImageCardDetails'));

const ArtistDashboard = lazy(() => import('./components/artist/Dashboard/ArtistDashboard'));
const UserProfile = lazy(() => import('./components/profile/ManageAccount'));
const AccountSetup = lazy(() => import('./components/profile/AccountSetup/AccountSetup'));
const ImageEditor = lazy(() => import('./components/common/ImageEditor/ImageEditor2'));
const ArtistGeneratedImageDetail = lazy(() => import('./components/artist/Dashboard/Images/ImageDetails'));
const ArtistProfileEdit = lazy(() => import('./components/artist/profile/ArtistProfileEdit'));

const PrivacyPolicy = lazy(() => import('./landingPage/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./landingPage/TermsOfUse'));
const FAQs = lazy(() => import('./landingPage/FAQs'));
const Pricing = lazy(() => import('./landingPage/Pricing'));

// Full-page redirect: a client-side <Navigate to="/#pricing"> loses the hash because the
// landing page's useNavigation() immediately re-navigates signed-out visitors to '/'.
const PricingSectionRedirect = () => {
  useEffect(() => {
    window.location.replace('/#pricing');
  }, []);
  return null;
};

export default function AppRoutes() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <Routes>
        <Route />
        <Route
          path="/"
          element={
            <Suspense fallback={<Loading />}>
              <BlogProvider>
                <LandingPage />
              </BlogProvider>
            </Suspense>
          }
        />
        <Route path="/b2b" element={<B2BPage />} />
        <Route
          path="/brands"
          element={
            <Suspense fallback={<Loading />}>
              <BlogProvider>
                <OriginalLandingPage />
              </BlogProvider>
            </Suspense>
          }
        />
        <Route
          path="/human-artists"
          element={
            <BlogProvider>
              <Suspense fallback={<Loading />}>
                <SLPArtists />
              </Suspense>
            </BlogProvider>
          }
        />

        <Route
          path="/ugc-creators"
          element={
            <BlogProvider>
              <Suspense fallback={<Loading />}>
                <SLPInfluencers />
              </Suspense>
            </BlogProvider>
          }
        />

        {/* Legacy redirect — keep old URL bookmarks/external links working */}
        <Route path="/human-influencers" element={<Navigate to="/ugc-creators" replace />} />

        <Route
          path="/blog"
          element={
            <BlogProvider>
              <Suspense fallback={<Loading />}>
                <Blogs />
              </Suspense>
            </BlogProvider>
          }
        />

        <Route
          path="/blog/:slug"
          element={
            <BlogProvider>
              <Suspense fallback={<Loading />}>
                <BlogDetails />
              </Suspense>
            </BlogProvider>
          }
        />

        <Route
          path="/blog"
          element={
            <BlogProvider>
              <Suspense fallback={<Loading />}>
                <Blogs />
              </Suspense>
            </BlogProvider>
          }
        />

        <Route
          path="/blog/:slug"
          element={
            <BlogProvider>
              <Suspense fallback={<Loading />}>
                <BlogDetails />
              </Suspense>
            </BlogProvider>
          }
        />

        <Route
          path="sign-in"
          element={
            <Suspense fallback={<Loading />}>
              <SignIn />
            </Suspense>
          }
        />

        <Route
          path="app/callback"
          element={
            <Suspense fallback={<Loading />}>
              <Callback />
            </Suspense>
          }
        />

        <Route
          path="privacy-policy"
          element={
            <Suspense fallback={<Loading />}>
              <PrivacyPolicy />
            </Suspense>
          }
        />

        <Route
          path="license-agreement-customer"
          element={
            <Suspense fallback={<Loading />}>
              <LicenseAgreementCustomer />
            </Suspense>
          }
        />

        <Route
          path="copyright-license-agreement-customer"
          element={
            <Suspense fallback={<Loading />}>
              <CopyrightLicenseAgreementCustomer />
            </Suspense>
          }
        />

        <Route
          path="terms-of-use"
          element={
            <Suspense fallback={<Loading />}>
              <TermsOfUse />
            </Suspense>
          }
        />

        <Route element={<AppProvider />}>
          <Route
            path="faq"
            element={
              <Suspense fallback={<Loading />}>
                <FAQs />
              </Suspense>
            }
          />

          {/* The standalone pricing page signs users in and fetches /subscriptions/v1/plans;
              while public sign-in is off, send visitors to the landing page's static pricing section. */}
          <Route
            path="pricing"
            element={
              PUBLIC_SIGN_IN_ENABLED ? (
                <Suspense fallback={<Loading />}>
                  <Pricing />
                </Suspense>
              ) : (
                <PricingSectionRedirect />
              )
            }
          />
        </Route>

        <Route
          path="copyright-license-agreement-artist"
          element={
            <Suspense fallback={<Loading />}>
              <CopyrightLicenseAgreementArtist />
            </Suspense>
          }
        />

        <Route
          path="license-agreement-influencer"
          element={
            <Suspense fallback={<Loading />}>
              <LicenseAgreementInfluencer />
            </Suspense>
          }
        />

        <Route>
          <Route element={<SignupProvider />}>
            {/* plan-setup route outside SignupFrameLayout for full-page layout */}
            <Route
              path="signup/plan-setup"
              element={
                <Suspense fallback={<Loading />}>
                  <ChoosePlan />
                </Suspense>
              }
            />
            
            <Route element={<SignupFrameLayout />}>
              <Route path="signup" element={<Outlet />}>
                <Route index element={<Navigate to="select-profile" />} />
                <Route
                  path="select-profile"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SignupAs />
                    </Suspense>
                  }
                />

                <Route
                  path="account-type"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SignupAccountType />
                    </Suspense>
                  }
                />
                <Route
                  path="choose-categories"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SignupChooseCategory />
                    </Suspense>
                  }
                />
                <Route
                  path="choose-collaborations"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SignupChooseCollaboration />
                    </Suspense>
                  }
                />

                <Route
                  path="upload-cover-photo"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SignupUploadCoverPhoto />
                    </Suspense>
                  }
                />

                <Route
                  path="upload-headshot"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SignupUploadHeadshot />
                    </Suspense>
                  }
                />
                <Route
                  path="social-accounts"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SignupSocialAccounts />
                    </Suspense>
                  }
                />

                <Route
                  path="artstyle-agreement"
                  element={
                    <Suspense fallback={<Loading />}>
                      <ArtStyleAgreement />
                    </Suspense>
                  }
                />

                <Route
                  path="pricing-setup"
                  element={
                    <Suspense fallback={<Loading />}>
                      <SignupPricingSetup />
                    </Suspense>
                  }
                />

                <Route
                  element={
                    <FintueArtstyleProvider>
                      <Outlet />
                    </FintueArtstyleProvider>
                  }
                >
                  <Route
                    path="finetune-model"
                    element={
                      <Suspense fallback={<Loading />}>
                        <SignupFinetuneModel />
                      </Suspense>
                    }
                  />

                  <Route
                    path="artstyle-pricing"
                    element={
                      <Suspense fallback={<Loading />}>
                        <SignupArtStylePricing />
                      </Suspense>
                    }
                  />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>

        <Route element={<AppProvider />}>
          <Route element={<InfluencerProvider />}>
            <Route element={<CreatorProvider />}>
              <Route element={<FrameLayout />}>
                <Route
                  path="image-editor"
                  element={
                    <Suspense fallback={<Loading />}>
                      <ImageEditor />
                    </Suspense>
                  }
                />
                <Route path="account">
                  <Route
                    index
                    element={
                      <Suspense fallback={<Loading />}>
                        <UserProfile />
                      </Suspense>
                    }
                  />

                  <Route
                    path="setup"
                    element={
                      <Suspense fallback={<Loading />}>
                        <AccountSetup />
                      </Suspense>
                    }
                  />
                </Route>
                <Route
                  path="models/image"
                  element={
                    <Suspense fallback={<Loading />}>
                      <ModelsImagePage />
                    </Suspense>
                  }
                />
                <Route
                  path="multimodel-support"
                  element={
                    <Suspense fallback={<Loading />}>
                      <MultiModelSupport/>
                    </Suspense>
                  }
                />
                <Route
                  path="models/video"
                  element={
                    <Suspense fallback={<Loading />}>
                      <ModalVideoPage />
                    </Suspense>
                  }
                />
              </Route>
            </Route>
          </Route>

          <Route element={<InfluencerProvider />}>
            <Route element={<FrameLayout />}>
              <Route path="influencer">
                <Route index element={<Navigate to="/influencer/dashboard" />} />
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<Loading />}>
                      <InfluencerDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="edit"
                  element={
                    <Suspense fallback={<Loading />}>
                      <InfluencerProfileEdit />
                    </Suspense>
                  }
                />
                <Route
                  path="license-request/:projectId"
                  element={
                    <Suspense fallback={<Loading />}>
                      <InfluencerLicenseRequest />
                    </Suspense>
                  }
                />
                <Route
                  path="license-request/:projectId/influencer_content_detail/:contentId"
                  element={
                    <Suspense fallback={<Loading />}>
                      <InfluencerContentDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="license-request/generated-images/details"
                  element={
                    <Suspense fallback={<Loading />}>
                      <GeneratedImageApprovalDetails />
                    </Suspense>
                  }
                />
              </Route>
              <Route
                path="test-page"
                element={
                  <Suspense fallback={<Loading />}>
                    <InfluencerProfileEdit />
                  </Suspense>
                }
              />
              <Route
                path="creator-profile"
                element={
                  <Suspense fallback={<Loading />}>
                    <CreatorProfileEdit />
                  </Suspense>
                }
              />
            </Route>
          </Route>

          <Route element={<CreatorProvider />}>
            <Route element={<FrameLayout />}>
              <Route path="creator">
                <Route index element={<Navigate to="/creator/dashboard" />} />
                <Route
                  path="dashboard"
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreatorDashboard />
                    </Suspense>
                  }
                />
                <Route
                  path="marketplace"
                  element={
                    <Suspense fallback={<Loading />}>
                      <Marketplace />
                    </Suspense>
                  }
                />
                <Route
                  path="project/:projectId"
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreatorProject />
                    </Suspense>
                  }
                />
                <Route
                  path="project/:projectId/creator_content_detail/:contentId"
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreatorContentDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="influencer/:influencerId"
                  element={
                    <Suspense fallback={<Loading />}>
                      <ViewInfluencer />
                    </Suspense>
                  }
                />
                <Route
                  path="influencer/:influencerId/create-project"
                  element={
                    <Suspense fallback={<Loading />}>
                      <CreateProject />
                    </Suspense>
                  }
                />

                <Route
                  path="images/detail"
                  element={
                    <Suspense fallback={<Loading />}>
                      <ArtistGeneratedImageDetail />
                    </Suspense>
                  }
                />
                <Route
                  path="project/generated-image-detail"
                  element={
                    <Suspense fallback={<Loading />}>
                      <GeneratedImageCardDetail />
                    </Suspense>
                  }
                />
              </Route>
            </Route>
          </Route>
          <Route>
            <Route element={<ArtistProvider />}>
              <Route
                element={
                  <FintueArtstyleProvider>
                    <Outlet />
                  </FintueArtstyleProvider>
                }
              >
                <Route element={<FrameLayout />}>
                  <Route path="artist">
                    <Route index element={<Navigate to="/artist/dashboard" />} />

                    <Route
                      path="dashboard"
                      element={
                        <Suspense fallback={<Loading />}>
                          <ArtistDashboard />
                        </Suspense>
                      }
                    />

                    <Route
                      path="images/detail"
                      element={
                        <Suspense fallback={<Loading />}>
                          <ArtistGeneratedImageDetail />
                        </Suspense>
                      }
                    />

                    <Route
                      path="artist-profile"
                      element={
                        <Suspense fallback={<Loading />}>
                          <ArtistProfileEdit />
                        </Suspense>
                      }
                    />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>

        <Route element={<AppProvider />}>
          <Route
            path="canvas"
            element={
              <Suspense fallback={<Loading />}>
                <CanvaContextProvider>
                  <CanvaGenerate />
                </CanvaContextProvider>
              </Suspense>
            }
          ></Route>

          <Route
            path="fairgen-ai-studio"
            element={
              <Suspense fallback={<Loading />}>
                <CanvaContextProvider>
                  <CanvaGenerateV2 />
                </CanvaContextProvider>
              </Suspense>
            }
          ></Route>
          <Route
            path="fairgen-ai-studio/personas"
            element={
              <Suspense fallback={<Loading />}>
                <CanvaContextProvider>
                  <PersonasSelectionPage />
                </CanvaContextProvider>
              </Suspense>
            }
          ></Route>
          <Route
            path="fairgen-ai-studio/art-styles"
            element={
              <Suspense fallback={<Loading />}>
                <CanvaContextProvider>
                  <ArtStylesSelectionPage />
                </CanvaContextProvider>
              </Suspense>
            }
          ></Route>
          <Route
            path="canvas-ai-editor"
            element={
              <Suspense fallback={<Loading />}>
                <CanvaContextProvider>
                  <CanvasAIEditor exiteBoolean={true} canvaBoardDisplay="grid" />
                </CanvaContextProvider>
              </Suspense>
            }
          ></Route>
        </Route>

        <Route
          element={
            <WhitelistProvider>
              <Outlet />
            </WhitelistProvider>
          }
        >
          <Route path="images">
            <Route
              index
              element={
                <Suspense fallback={<Loading />}>
                  <DashboardImages />
                </Suspense>
              }
            />
            <Route
              path="detail"
              element={
                <Suspense fallback={<Loading />}>
                  <GeneratedImageDetail />
                </Suspense>
              }
            />
          </Route>
        </Route>

        <Route path="/admin/features" element={<FeatureManagementPage />} />

        <Route
          path="*"
          element={
            <Suspense fallback={<Loading />}>
              <NotFound />
            </Suspense>
          }
        />
      </Routes>
    </Router>
  );
}
